import { memo } from 'react';
import { ChevronLeft, ChevronRight, Download, Keyboard } from 'lucide-react';
import { FavoriteButton } from '@/components/FavoriteButton';
import { MiniPlayer } from './MiniPlayer';
import { FILE_TYPE_CONFIG, type FileType } from '@/types/library';
import type { FavoriteCachedData } from '@/types/favorites';

interface ViewerSidebarProps {
  type: FileType;
  title?: string;
  fileId?: string;
  favoriteCachedData?: FavoriteCachedData;
  onDownload?: () => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  tags?: string[];
  fileSize?: number;
  thumbnailUrl?: string;
}

function ViewerSidebarInner({
  type,
  title,
  fileId,
  favoriteCachedData,
  onDownload,
  currentPage,
  totalPages,
  onPageChange,
  tags,
  fileSize,
  thumbnailUrl,
}: ViewerSidebarProps) {
  const typeConfig = FILE_TYPE_CONFIG[type] || FILE_TYPE_CONFIG.other;
  const hasPages = currentPage != null && totalPages != null && totalPages > 1 && onPageChange;
  const maxDots = 5;
  const showDots = (totalPages ?? 0) <= maxDots;

  return (
    <div className="w-80 bg-white border-l border-[#f0d2f0]/50 flex flex-col shrink-0">
      {/* Thumbnail / Preview */}
      {thumbnailUrl && (
        <div className="p-4 border-b border-[#f0d2f0]/30">
          <img
            src={thumbnailUrl}
            alt={title || ''}
            className="w-full h-36 rounded-xl object-cover bg-[#faf8f5]"
          />
        </div>
      )}

      {/* File details */}
      <div className="p-4 space-y-3 border-b border-[#f0d2f0]/30">
        <h3 className="text-[#5a3d58] font-semibold text-sm">{title || 'Untitled'}</h3>

        <div className="flex flex-wrap gap-1.5">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full ${typeConfig.bgColor} ${typeConfig.color}`}
          >
            {typeConfig.label}
          </span>
          {totalPages != null && totalPages > 0 && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-[#f8e8f8] text-[#8e4585]">
              {totalPages} {totalPages === 1 ? 'page' : 'pages'}
            </span>
          )}
          {fileSize != null && fileSize > 0 && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-slate-100 text-slate-500">
              {formatFileSize(fileSize)}
            </span>
          )}
        </div>

        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-[10px] rounded-full bg-[#f8e8f8] text-[#8e4585]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Page navigation */}
      {hasPages && (
        <div className="p-4 border-b border-[#f0d2f0]/30 space-y-2">
          <h4 className="text-[#5a3d58] text-xs font-semibold uppercase tracking-wider">
            Page Navigation
          </h4>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-[#f8e8f8] text-[#8e4585] text-xs font-medium disabled:opacity-30 disabled:cursor-not-allowed active:bg-[#f0d2f0] transition-colors"
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-[#8e4585] text-white text-xs font-medium disabled:opacity-30 disabled:cursor-not-allowed active:bg-[#63345e] transition-colors"
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
          <div className="flex items-center justify-center gap-1.5">
            {showDots &&
              Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => onPageChange(i + 1)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i + 1 === currentPage ? 'bg-[#8e4585]' : 'bg-[#e5bce5] hover:bg-[#d4a5d4]'
                  }`}
                  aria-label={`Go to page ${i + 1}`}
                />
              ))}
            <span className="text-[#5a3d58] text-[10px] font-medium ml-1">
              {currentPage} / {totalPages}
            </span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="p-4 space-y-2 border-b border-[#f0d2f0]/30">
        {fileId && favoriteCachedData && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#f8e8f8] transition-colors">
            <FavoriteButton
              itemId={fileId}
              itemType="file"
              cachedData={favoriteCachedData}
              size="md"
            />
            <span className="text-[#5a3d58] text-xs">Add to Favorites</span>
          </div>
        )}
        {onDownload && (
          <button
            onClick={onDownload}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[#5a3d58] hover:bg-[#f8e8f8] transition-colors text-xs"
          >
            <Download size={16} className="text-[#8e4585]" />
            Download File
          </button>
        )}
      </div>

      {/* Media player */}
      <div className="flex-1" />
      <MiniPlayer />

      {/* Keyboard hints */}
      <div className="px-4 py-3 border-t border-[#f0d2f0]/30">
        <div className="flex items-center gap-1.5 text-[#8e4585]/50 text-[9px]">
          <Keyboard size={10} />
          <span>Arrow keys • +/- zoom • R rotate • Esc close</span>
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const ViewerSidebar = memo(ViewerSidebarInner);
